import { useEffect, useState, useRef, useCallback } from "react";
import { getOrderMessages, sendOrderMessage, markOrderMessagesRead } from "../api/client";
import { subscribeOrderChat } from "../api/socket";

const CUSTOMER_QUICK_CHIPS = [
  "I'm waiting outside",
  "Please call when you arrive",
  "Drop with security",
  "How far are you?"
];

const RIDER_QUICK_CHIPS = [
  "I've arrived at pickup",
  "On my way to dropoff",
  "I'm at the gate/entrance",
  "Heavy traffic on the road"
];

export default function OrderChatModal({
  order,
  currentRole = "user", // "user" | "rider"
  counterpartyName = "",
  isOpen: controlledIsOpen,
  onOpen,
  onClose,
  defaultOpen = false
}) {
  const orderId = order?._id;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalOpen;

  const setIsOpen = (val) => {
    setInternalOpen(val);
    if (val && onOpen) onOpen();
    if (!val && onClose) onClose();
  };
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const displayName = counterpartyName || (
    currentRole === "user"
      ? (order?.rider?.name || "Dispatcher")
      : (order?.pickupContact?.name || order?.user?.name || "Customer")
  );

  const chips = currentRole === "user" ? CUSTOMER_QUICK_CHIPS : RIDER_QUICK_CHIPS;

  // Scroll to bottom of message thread
  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
    }
  }, []);

  // Fetch message history on mount
  useEffect(() => {
    if (!orderId) return;
    let mounted = true;

    async function fetchMessages() {
      try {
        const { data } = await getOrderMessages(orderId);
        if (mounted) {
          setMessages(data || []);
          const unread = (data || []).filter(
            (m) => m.senderRole !== currentRole && !m.read
          ).length;
          setUnreadCount(unread);
        }
      } catch (err) {
        console.warn("[OrderChat] Failed to fetch messages:", err.message);
      }
    }

    fetchMessages();
    return () => { mounted = false; };
  }, [orderId, currentRole]);

  // Real-time socket listener for incoming chat messages
  useEffect(() => {
    if (!orderId) return;

    const unsubscribe = subscribeOrderChat(orderId, {
      onMessage: ({ message }) => {
        if (!message) return;
        setMessages((prev) => {
          // Avoid duplicate insertion
          if (prev.some((m) => m._id === message._id)) return prev;
          return [...prev, message];
        });

        if (message.senderRole !== currentRole) {
          if (isOpen) {
            // Mark as read immediately if chat is already open
            markOrderMessagesRead(orderId).catch(() => {});
          } else {
            setUnreadCount((c) => c + 1);
          }
        }
      },
      onRead: ({ readByRole }) => {
        if (readByRole !== currentRole) {
          // Other party read our messages
          setMessages((prev) =>
            prev.map((m) => (m.senderRole === currentRole ? { ...m, read: true } : m))
          );
        }
      }
    });

    return unsubscribe;
  }, [orderId, currentRole, isOpen]);

  // When chat opens, mark messages as read and scroll to bottom
  useEffect(() => {
    if (isOpen) {
      scrollToBottom(false);
      if (unreadCount > 0) {
        setUnreadCount(0);
        markOrderMessagesRead(orderId).catch(() => {});
      }
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen, orderId, unreadCount, scrollToBottom]);

  // Scroll down whenever messages change while open
  useEffect(() => {
    if (isOpen) {
      scrollToBottom(true);
    }
  }, [messages, isOpen, scrollToBottom]);

  const handleSend = async (messageText) => {
    const clean = (messageText || text).trim();
    if (!clean || sending || !orderId) return;

    setText("");
    setSending(true);

    try {
      const { data: savedMsg } = await sendOrderMessage(orderId, clean);
      setMessages((prev) => {
        if (prev.some((m) => m._id === savedMsg._id)) return prev;
        return [...prev, savedMsg];
      });
      scrollToBottom(true);
    } catch (err) {
      console.error("[OrderChat] Send failed:", err.message);
      setText(clean); // restore if failed
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format timestamp helper
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      {/* Backdrop overlay when open */}
      {isOpen && (
        <div
          className="order-chat-backdrop"
          onClick={() => setIsOpen(false)}
          aria-label="Close Delivery Chat"
        />
      )}

      {/* Floating Toggle Button */}
      {!isOpen && (
        <div className="order-chat-fab-wrapper">
          <button
            type="button"
            className="order-chat-fab"
            onClick={() => setIsOpen(true)}
            aria-label="Open Delivery Chat"
          >
            <span className="order-chat-fab-icon">💬</span>
            <span className="order-chat-fab-text">
              Chat with {displayName}
            </span>
            {unreadCount > 0 && (
              <span className="order-chat-badge">{unreadCount}</span>
            )}
          </button>
        </div>
      )}

      {/* Slide-over / Popup Chat Box */}
      {isOpen && (
        <div className="order-chat-panel">
          {/* Header */}
          <div className="order-chat-header">
            <div className="order-chat-header-info">
              <div className="order-chat-avatar">
                {currentRole === "user" ? "🛵" : "👤"}
              </div>
              <div>
                <div className="order-chat-title">{displayName}</div>
                <div className="order-chat-subtitle">
                  <span className="order-chat-status-dot" />
                  Live delivery chat
                </div>
              </div>
            </div>
            <button
              type="button"
              className="order-chat-close-btn"
              onClick={() => setIsOpen(false)}
              title="Close chat"
            >
              ✕
            </button>
          </div>

          {/* Quick reply chips */}
          <div className="order-chat-chips">
            {chips.map((chip, idx) => (
              <button
                key={idx}
                type="button"
                className="order-chat-chip"
                onClick={() => handleSend(chip)}
                disabled={sending}
              >
                {chip}
              </button>
            ))}
          </div>

          {/* Message Thread */}
          <div className="order-chat-messages">
            {messages.length === 0 ? (
              <div className="order-chat-empty">
                <div style={{ fontSize: "28px", marginBottom: "6px" }}>💬</div>
                <p style={{ margin: 0, fontWeight: 600 }}>Direct Coordination</p>
                <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "var(--text-muted)" }}>
                  Send updates, gate instructions, or location details.
                </p>
              </div>
            ) : (
              messages.map((msg, index) => {
                const isMine = msg.senderRole === currentRole;
                return (
                  <div
                    key={msg._id || index}
                    className={`order-chat-bubble-row ${isMine ? "mine" : "theirs"}`}
                  >
                    <div className={`order-chat-bubble ${isMine ? "mine" : "theirs"}`}>
                      <div className="order-chat-text">{msg.text}</div>
                      <div className="order-chat-meta">
                        <span className="order-chat-time">{formatTime(msg.createdAt)}</span>
                        {isMine && (
                          <span className={`order-chat-ticks ${msg.read ? "read" : ""}`}>
                            {msg.read ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Bar */}
          <div className="order-chat-input-bar">
            <input
              ref={inputRef}
              type="text"
              className="order-chat-input"
              placeholder={`Message ${displayName}...`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={1000}
              disabled={sending}
            />
            <button
              type="button"
              className="order-chat-send-btn"
              onClick={() => handleSend()}
              disabled={!text.trim() || sending}
              aria-label="Send message"
            >
              {sending ? "…" : "➤"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
